'use client';

import { useState, useEffect, useRef } from 'react';
import { X, RefreshCw, Download } from 'lucide-react';
import { api } from '@/utils/api';

// Função para detectar se é mobile
const isMobile = () => {
    if (typeof window === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
};

// Só mostrar banner de atualização quando estiver no PWA (aberto da tela inicial / instalado)
const isPWA = () => {
    if (typeof window === 'undefined') return false;
    const nav = window.navigator as Navigator & { standalone?: boolean };
    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        (nav.standalone === true) ||
        document.referrer.includes('android-app://')
    );
};

const WEB_VERSION_KEY = 'web_app_version';

export default function PWAUpdater() {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [isInstalling, setIsInstalling] = useState(false);
    const [showInstallPrompt, setShowInstallPrompt] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const backendWebVersionRef = useRef<string | null>(null);

    // Ajustar navbar quando banner estiver visível
    useEffect(() => {
        if (updateAvailable) {
            document.body.style.setProperty('--banner-height', '80px');
            document.documentElement.classList.add('banner-visible');
        } else {
            document.body.style.removeProperty('--banner-height');
            document.documentElement.classList.remove('banner-visible');
        }
        return () => {
            document.body.style.removeProperty('--banner-height');
            document.documentElement.classList.remove('banner-visible');
        };
    }, [updateAvailable]);

    // Checar versão do backend (forçar atualização quando você sobe WEB_APP_VERSION no servidor)
    // Só mostra o banner quando estiver no PWA (standalone), não no navegador normal
    useEffect(() => {
        if (!isPWA()) return;
        let cancelled = false;
        api.get('app-version/web').then((res) => {
            if (cancelled || typeof window === 'undefined') return;
            const v = (res.data as { version?: string } | undefined)?.version;
            if (!v) return;
            const stored = localStorage.getItem(WEB_VERSION_KEY);
            if (stored !== v) {
                backendWebVersionRef.current = v;
                setUpdateAvailable(true);
            }
        }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        // Só ativar PWA no mobile
        if (!isMobile()) {
            return;
        }

        // Detectar atualizações do Service Worker (banner de atualização só no PWA)
        if ('serviceWorker' in navigator) {
            let registration: ServiceWorkerRegistration | null = null;

            navigator.serviceWorker.ready.then((reg) => {
                registration = reg;
                
                // Verificar atualizações periodicamente
                const checkForUpdates = () => {
                    reg.update();
                };

                // Verificar a cada 5 minutos
                const interval = setInterval(checkForUpdates, 5 * 60 * 1000);

                // Verificar quando a página ganha foco (usuário volta para o app)
                const handleFocus = () => {
                    reg.update();
                };
                window.addEventListener('focus', handleFocus);

                // Listener para quando uma nova versão é encontrada (só mostrar banner se estiver no PWA)
                const handleControllerChange = () => {
                    if (isPWA()) setUpdateAvailable(true);
                };
                navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

                // Verificar se há uma nova versão esperando (só mostrar banner se estiver no PWA)
                const handleUpdateFound = () => {
                    const newWorker = reg.installing;
                    if (newWorker) {
                        newWorker.addEventListener('statechange', () => {
                            if (newWorker.state === 'installed' && navigator.serviceWorker.controller && isPWA()) {
                                setUpdateAvailable(true);
                            }
                        });
                    }
                };
                reg.addEventListener('updatefound', handleUpdateFound);

                // Verificar atualizações imediatamente
                reg.update();

                return () => {
                    clearInterval(interval);
                    window.removeEventListener('focus', handleFocus);
                    navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
                    reg.removeEventListener('updatefound', handleUpdateFound);
                };
            });

            // Detectar prompt de instalação (apenas mobile)
            const handleBeforeInstallPrompt = (e: Event) => {
                if (!isMobile()) {
                    return; // Ignorar no desktop
                }
                e.preventDefault();
                setDeferredPrompt(e);
                setShowInstallPrompt(true);
            };

            window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

            // Verificar se já está instalado
            if (window.matchMedia('(display-mode: standalone)').matches) {
                setShowInstallPrompt(false);
            }

            return () => {
                window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            };
        }
    }, []);

    const handleUpdate = async () => {
        if (backendWebVersionRef.current) {
            localStorage.setItem(WEB_VERSION_KEY, backendWebVersionRef.current);
        }
        if ('serviceWorker' in navigator) {
            setIsInstalling(true);
            try {
                const registration = await navigator.serviceWorker.getRegistration();
                const doReload = () => window.location.reload();

                if (registration?.waiting) {
                    // SW novo já está em "waiting" - pedir ativação e recarregar quando o controller mudar
                    const onControllerChange = () => {
                        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
                        doReload();
                    };
                    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
                    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                    // Fallback: se controllerchange não disparar em 3s, recarrega mesmo assim
                    setTimeout(() => {
                        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
                        doReload();
                    }, 3000);
                } else {
                    // Forçar verificação de atualização
                    await registration?.update();
                    // Se apareceu um waiting após o update, ativar ele
                    if (registration?.waiting) {
                        const onControllerChange = () => {
                            navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
                            doReload();
                        };
                        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
                        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                        setTimeout(() => {
                            navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
                            doReload();
                        }, 3000);
                    } else {
                        doReload();
                    }
                }
            } catch (error) {
                console.error('Erro ao atualizar:', error);
                setIsInstalling(false);
            }
        } else {
            window.location.reload();
        }
    };

    const handleInstall = async () => {
        if (!deferredPrompt) return;

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
            setShowInstallPrompt(false);
        }
        
        setDeferredPrompt(null);
    };

    if (!updateAvailable && !showInstallPrompt) {
        return null;
    }

    return (
        <>
            {/* Banner de Atualização */}
            {updateAvailable && (
                <div className="fixed top-0 left-0 right-0 z-[99999] bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-2xl border-b border-orange-400/50 animate-slide-down">
                    <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                                <RefreshCw className="w-5 h-5 animate-spin" />
                            </div>
                            <div>
                                <p className="font-bold text-sm">Nova versão disponível!</p>
                                <p className="text-xs text-white/90">Atualize para ter a melhor experiência</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleUpdate}
                                disabled={isInstalling}
                                className="px-4 py-2 bg-white text-orange-600 font-bold rounded-lg hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                            >
                                {isInstalling ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Atualizando...
                                    </>
                                ) : (
                                    <>
                                        <Download className="w-4 h-4" />
                                        Atualizar
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => setUpdateAvailable(false)}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                aria-label="Fechar"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Banner de Instalação */}
            {showInstallPrompt && !updateAvailable && (
                <div className="fixed bottom-0 left-0 right-0 z-[99999] bg-gradient-to-r from-neutral-900 to-neutral-800 text-white shadow-2xl border-t border-white/10 animate-slide-up">
                    <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                                <Download className="w-5 h-5 text-orange-500" />
                            </div>
                            <div>
                                <p className="font-bold text-sm">Instale o MediocreToons</p>
                                <p className="text-xs text-white/70">Adicione à tela inicial para acesso rápido</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleInstall}
                                className="px-4 py-2 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-500 transition-colors flex items-center gap-2 text-sm"
                            >
                                <Download className="w-4 h-4" />
                                Instalar
                            </button>
                            <button
                                onClick={() => setShowInstallPrompt(false)}
                                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                aria-label="Fechar"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

